import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { PASSWORD_RULES, passwordScore } from "../lib/passwordRules";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { clearPasswordRecovery, isPasswordRecovery, loading } = useAuth();

  const allRulesOk = PASSWORD_RULES.every((r) => r.ok(newPassword));

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRulesOk) return;

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setMessage(`❌ Fehler: ${error.message}`);
    } else {
      clearPasswordRecovery();
      setMessage("✅ Passwort erfolgreich geändert. Du wirst weitergeleitet...");
      setTimeout(() => navigate("/dashboard"), 3000);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        Laden…
      </div>
    );
  }

  if (!isPasswordRecovery) {
    return (
      <div className="h-screen bg-[#0d1117] flex flex-col items-center justify-center gap-4 p-4">
        <p className="text-red-400 font-medium">Link ungültig oder abgelaufen.</p>
        <a href="/" className="text-sky-400 hover:underline">Zurück zur Startseite</a>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0d1117] flex items-center justify-center p-4">
      <form
        onSubmit={handleNewPassword}
        className="bg-[#161b27] border border-[#2a3347] p-8 rounded-2xl shadow-lg space-y-4 w-full max-w-sm"
      >
        <h1 className="text-2xl font-bold text-slate-100 text-center mb-4">
          Neues Passwort setzen
        </h1>

        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Neues Passwort"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded pr-10 outline-none focus:border-sky-400 placeholder:text-slate-600"
            required
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-300 text-sm transition-colors"
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        {/* Passwort-Checkliste */}
        {newPassword.length > 0 && (
          <div className="text-sm" aria-live="polite">
            <ul className="space-y-1">
              {PASSWORD_RULES.map((rule) => {
                const ok = rule.ok(newPassword);
                return (
                  <li key={rule.id} className="flex items-center">
                    <span className={`mr-2 ${ok ? "text-emerald-400" : "text-slate-500"}`}>
                      {ok ? "✅" : "⬜"}
                    </span>
                    <span className={ok ? "text-emerald-400" : "text-slate-400"}>
                      {rule.label}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 h-1 w-full bg-[#2a3347] rounded">
              <div
                className="h-1 rounded transition-all"
                style={{
                  width: `${(passwordScore(newPassword) / PASSWORD_RULES.length) * 100}%`,
                  backgroundColor:
                    passwordScore(newPassword) < 3 ? "#ef4444" :
                    passwordScore(newPassword) < 5 ? "#f59e0b" : "#22c55e",
                }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!allRulesOk}
          className="w-full bg-sky-500 text-white py-2 rounded hover:bg-sky-600 transition-colors disabled:opacity-50"
        >
          Passwort aktualisieren
        </button>

        {message && <p className="text-center mt-4 text-slate-300">{message}</p>}
      </form>
    </div>
  );
}
